/**
 * Community Financial Impact Model — 4-channel population-scale ROI.
 *
 * Channels:
 * 1. Health Outcomes — avoided healthcare costs
 * 2. Economic Activation — property value, retail, jobs
 * 3. Quality of Life — social isolation, physical activity, cognitive function
 * 4. Climate Resilience — UHI, stormwater, carbon sequestration
 */

// ── Evidence-backed coefficients ──

const HEALTH_OUTCOMES = {
  // Air quality: WHO dose-response — $150/µg/m³ per capita per year (conservative)
  pm25_cost_per_ug_per_capita: 150,
  // Green space mental health: OR 0.72 for depression with 300m access (Gascon 2015)
  greenSpace_depression_OR: 0.72,
  depression_prevalence: 0.08,
  depression_annual_cost: 10000,
  // UHI: 1.5 ER visits per 100k per °C above threshold (Basu 2009)
  uhi_er_visits_per_100k_per_degC: 1.5,
  er_visit_cost: 2500,
  uhi_reduction_per_pct_canopy: 0.3,
  // Physical activity from walkability: 30 min/week increase per 10 Walk Score points
  walkability_activity_minutes_per_10ws: 30,
  physical_inactivity_cost_per_capita: 1437,
};

const ECONOMIC_ACTIVATION = {
  // Property value: 1-3% increase per 10 Walk Score points (Pivo & Fisher 2011)
  walkScore_property_value_pct_per_10: 0.015,
  // Retail: $30 revenue per additional pedestrian per day per storefront (Project for Public Spaces)
  foot_traffic_revenue_per_ped_day: 30,
  activation_foot_traffic_multiplier: 2.0,
  // Mixed-use density: jobs per acre estimate
  mixedUse_jobs_per_acre_factor: 5,
};

const QUALITY_OF_LIFE = {
  // Social isolation: 26% reduction with community gathering spaces (Fakoya 2020)
  social_infra_isolation_reduction: 0.26,
  isolation_prevalence: 0.15,
  isolation_annual_health_cost: 6500,
  // QALY from physical activity: $50k/QALY, 0.03 QALY per active person per year
  qaly_value: 50000,
  active_person_qaly_gain: 0.03,
  // Cognitive function: 20% reduced dementia risk with 30 min daily walking (Livingston 2020)
  dementia_risk_reduction_walkable: 0.20,
  dementia_prevalence_over65: 0.10,
  dementia_annual_cost: 60000,
};

const CLIMATE_RESILIENCE = {
  // Tree canopy UHI reduction: 0.3°C per % canopy
  uhi_reduction_per_pct_canopy: 0.3,
  cooling_cost_per_degC_per_acre: 50,
  // Stormwater: $0.20 per gallon capacity from permeable surfaces
  stormwater_value_per_pct_permeable_per_acre: 200,
  // Carbon: 48 lbs CO2 per tree per year, ~100 trees per acre at 25% canopy
  carbon_per_tree_lbs_yr: 48,
  trees_per_acre_at_25pct: 25,
  social_cost_carbon_per_ton: 51,
};

/**
 * Compute 4-channel community impact.
 *
 * @param {Object} params
 * @param {Object} params.pathwayScores — from assessZone/assessDistrict
 * @param {Object} params.zoneData — raw zone inputs
 * @param {number} params.population — estimated population in zone
 * @param {number} params.areaAcres — zone area
 * @param {number} params.medianPropertyValue — local median (default $300k)
 * @param {number} params.pctOver65 — % population over 65 (default 15%)
 * @param {number} params.baselinePm25 — current PM2.5 if different from zone
 * @param {number} params.projectionYears — years to project (default 10)
 * @returns {Object} Impact breakdown by channel
 */
export function computeCommunityImpact({
  pathwayScores = {},
  zoneData = {},
  population = 5000,
  areaAcres = 100,
  medianPropertyValue = 300000,
  pctOver65 = 15,
  baselinePm25 = null,
  projectionYears = 10,
} = {}) {
  const h = HEALTH_OUTCOMES;
  const e = ECONOMIC_ACTIVATION;
  const q = QUALITY_OF_LIFE;
  const c = CLIMATE_RESILIENCE;

  const treeCanopy = zoneData.treeCanopyPct || 15;
  const walkScore = zoneData.walkScore || 50;
  const pm25 = zoneData.pm25Annual || 12;
  const greenAccess = zoneData.greenSpaceAccess300m || 50;
  const permeablePct = zoneData.permeableSurfacePct || 20;
  const groundActivation = zoneData.groundPlaneActivationPct || 40;
  const socialInfra = zoneData.socialInfraPerThousand || 1.5;
  const mixedUse = zoneData.mixedUseIndex || 0.4;

  // ── Channel 1: Health Outcomes ──

  // Air quality benefit: reduction from baseline
  const bpm = baselinePm25 || pm25;
  const pm25Improvement = Math.max(0, bpm - pm25);
  const airQualitySavings = pm25Improvement * h.pm25_cost_per_ug_per_capita * population;

  // Green space mental health
  const greenAccessFactor = Math.min(1, greenAccess / 90);
  const depressionReduction = (1 - h.greenSpace_depression_OR) * greenAccessFactor;
  const mentalHealthSavings = depressionReduction * h.depression_prevalence * population * h.depression_annual_cost;

  // UHI heat-related illness avoidance
  const uhiReduction = Math.max(0, treeCanopy - 10) * h.uhi_reduction_per_pct_canopy;
  const avoidedERvisits = (uhiReduction * h.uhi_er_visits_per_100k_per_degC * population) / 100000;
  const heatSavings = avoidedERvisits * h.er_visit_cost;

  const healthTotal = airQualitySavings + mentalHealthSavings + heatSavings;

  // ── Channel 2: Economic Activation ──

  // Property value uplift
  const walkScoreAbove50 = Math.max(0, walkScore - 50);
  const propertyUpliftPct = (walkScoreAbove50 / 10) * e.walkScore_property_value_pct_per_10;
  const totalPropertyValue = medianPropertyValue * (population / 2.5); // ~2.5 people per household
  const propertyValueGain = totalPropertyValue * propertyUpliftPct;
  const annualPropertyTax = propertyValueGain * 0.01; // ~1% property tax rate

  // Retail from ground-plane activation
  const activationScore = groundActivation / 100;
  const dailyFootTraffic = activationScore * e.activation_foot_traffic_multiplier * population * 0.1;
  const retailRevenue = dailyFootTraffic * e.foot_traffic_revenue_per_ped_day * 250; // working days

  // Jobs from mixed-use density
  const estimatedJobs = mixedUse * e.mixedUse_jobs_per_acre_factor * areaAcres;

  const economicTotal = annualPropertyTax + retailRevenue;

  // ── Channel 3: Quality of Life ──

  // Social isolation reduction
  const socialInfraScore = Math.min(1, socialInfra / 3.0);
  const isolationReduction = q.social_infra_isolation_reduction * socialInfraScore;
  const isolationSavings = isolationReduction * q.isolation_prevalence * population * q.isolation_annual_health_cost;

  // Physical activity QALY
  const additionalActiveMinutes = (walkScoreAbove50 / 10) * h.walkability_activity_minutes_per_10ws;
  const newlyActivePeople = Math.min(population * 0.3, population * (additionalActiveMinutes / 150)); // 150 min/week target
  const qalyValue = newlyActivePeople * q.active_person_qaly_gain * q.qaly_value;

  // Cognitive function (over 65)
  const pop65 = population * (pctOver65 / 100);
  const walkableReduction = walkScore >= 70 ? q.dementia_risk_reduction_walkable : q.dementia_risk_reduction_walkable * (walkScore / 70);
  const avoidedDementia = walkableReduction * q.dementia_prevalence_over65 * pop65;
  const cognitiveSavings = avoidedDementia * q.dementia_annual_cost;

  const qualityTotal = isolationSavings + qalyValue + cognitiveSavings;

  // ── Channel 4: Climate Resilience ──

  // UHI cooling
  const coolingSavings = uhiReduction * c.cooling_cost_per_degC_per_acre * areaAcres;

  // Stormwater management
  const stormwaterValue = permeablePct * c.stormwater_value_per_pct_permeable_per_acre * areaAcres / 100;

  // Carbon sequestration
  const treesEstimate = (treeCanopy / 25) * c.trees_per_acre_at_25pct * areaAcres;
  const carbonLbs = treesEstimate * c.carbon_per_tree_lbs_yr;
  const carbonValue = (carbonLbs / 2204.6) * c.social_cost_carbon_per_ton; // lbs to metric tons

  const climateTotal = coolingSavings + stormwaterValue + carbonValue;

  // ── Totals ──

  const totalAnnual = healthTotal + economicTotal + qualityTotal + climateTotal;

  return {
    annualImpact: totalAnnual,
    projectedImpact: totalAnnual * projectionYears,
    projectionYears,
    population,
    areaAcres,

    channels: {
      health: {
        label: 'Health Outcomes',
        total: healthTotal,
        color: '#EF4444',
        items: [
          { label: 'Air quality improvement', value: airQualitySavings, citation: 'WHO dose-response, Liu 2021' },
          { label: 'Mental health (green space access)', value: mentalHealthSavings, citation: 'Gascon 2015, OR 0.72' },
          { label: 'Heat-related illness avoidance', value: heatSavings, citation: 'Basu 2009, UHI research' },
        ],
      },
      economic: {
        label: 'Economic Activation',
        total: economicTotal,
        color: '#F59E0B',
        items: [
          { label: 'Property tax from walkability uplift', value: annualPropertyTax, citation: 'Pivo & Fisher 2011' },
          { label: 'Retail revenue from activation', value: retailRevenue, citation: 'Project for Public Spaces' },
          { label: `Estimated jobs created: ${Math.round(estimatedJobs)}`, value: 0, citation: 'Mixed-use density estimate' },
        ],
      },
      quality: {
        label: 'Quality of Life',
        total: qualityTotal,
        color: '#8B5CF6',
        items: [
          { label: 'Social isolation reduction', value: isolationSavings, citation: 'Fakoya 2020' },
          { label: 'Physical activity QALY gains', value: qalyValue, citation: 'Livingston 2020, QALY model' },
          { label: 'Cognitive function preservation', value: cognitiveSavings, citation: 'Livingston 2020' },
        ],
      },
      climate: {
        label: 'Climate Resilience',
        total: climateTotal,
        color: '#10B981',
        items: [
          { label: 'UHI cooling savings', value: coolingSavings, citation: 'EPA UHI research' },
          { label: 'Stormwater management value', value: stormwaterValue, citation: 'EPA Green Infrastructure' },
          { label: 'Carbon sequestration', value: carbonValue, citation: 'USFS i-Tree, SCC $51/ton' },
        ],
      },
    },
  };
}
